import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ScanLine } from "lucide-react";
import { api } from "../../lib/api";
import { Btn, Loading, ErrorState } from "./ui";
import { QrScanner } from "../../components/QrScanner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function CommercePanel() {
  const [tab, setTab] = useState("stats");
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [token, setToken] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [chartMode, setChartMode] = useState("day");

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const [o, t, s, st, ci] = await Promise.all([
        api.get("/admin/orders"), api.get("/admin/tickets"), api.get("/admin/subscriptions"),
        api.get("/admin/stats/sales"), api.get("/admin/checkins"),
      ]);
      setOrders(Array.isArray(o.data) ? o.data : []);
      setTickets(Array.isArray(t.data) ? t.data : []);
      setSubs(Array.isArray(s.data) ? s.data : []);
      setStats(st.data || null);
      setCheckins(Array.isArray(ci.data) ? ci.data : []);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadCheckins = async () => {
    try { const { data } = await api.get("/admin/checkins"); setCheckins(Array.isArray(data) ? data : []); }
    catch (e) {}
  };

  const refund = async (sid) => {
    if (!window.confirm("Rembourser cette commande ?")) return;
    try { await api.post(`/admin/orders/${sid}/refund`); toast.success("Remboursé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ventes-effervescence.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };
  const resendEmail = async (sid) => {
    try { await api.post(`/admin/orders/${sid}/resend-email`); toast.success("Email renvoyé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const handleScan = useCallback(async (value) => {
    if (!value) return;
    try {
      const { data } = await api.post("/admin/tickets/checkin", { token: value });
      setScanResult({ ...data, _ts: Date.now() });
      if (data.valid) toast.success(`Valide · ${data.event_title || ""}`);
      else toast.error(data.reason || "Refusé");
      loadCheckins();
    } catch {
      setScanResult({ valid: false, reason: "Erreur réseau", _ts: Date.now() });
      toast.error("Erreur");
    }
  }, []);
  const submitToken = async () => {
    if (!token) return;
    await handleScan(token);
    setToken("");
  };
  const undoCheckin = async (tok) => {
    if (!window.confirm("Annuler ce check-in ? Le billet redeviendra valide.")) return;
    try { await api.post(`/admin/tickets/${tok}/undo-checkin`); toast.success("Check-in annulé"); loadCheckins(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const money = (a, c) => `${Number(a || 0).toFixed(2)} ${(c || "cad").toUpperCase()}`;
  const daily = stats?.daily || [];
  const chartData = chartMode === "day"
    ? daily.map((d) => ({ label: d.date.slice(5), total: d.total }))
    : (() => {
        const out = [];
        for (let i = 0; i < daily.length; i += 7) {
          const chunk = daily.slice(i, i + 7);
          if (!chunk.length) continue;
          out.push({ label: chunk[0].date.slice(5), total: Math.round(chunk.reduce((s, x) => s + x.total, 0) * 100) / 100 });
        }
        return out;
      })();
  const tabs = [["stats", "Statistiques"], ["orders", "Commandes"], ["tickets", "Billets"], ["subs", "Abonnements"], ["checkin", "Check-in"], ["history", "Historique"]];

  return (
    <div>
      <h2 className="font-display text-3xl mb-8">Billetterie & Abonnements</h2>
      <div className="flex gap-6 border-b border-[#292929] pb-4 mb-8">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`commerce-tab-${k}`} onClick={() => setTab(k)}
            className={`text-[10px] tracking-[0.2em] uppercase ${tab === k ? "text-white" : "text-muted-eff hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {error && <div className="mb-6"><ErrorState onRetry={load} /></div>}

      {tab === "stats" && (
        <div data-testid="commerce-stats">
          {!stats && !error && <Loading />}
          {stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#292929] mb-10">
                {[
                  ["Billets vendus", String(stats.totals?.tickets ?? 0), "stat-tickets"],
                  ["Revenus billets", money(stats.totals?.ticket_revenue, stats.totals?.currency), "stat-ticket-revenue"],
                  ["Revenus abonnements", money(stats.totals?.sub_revenue, stats.totals?.currency), "stat-sub-revenue"],
                  ["Abonnés actifs", String(stats.totals?.active_subs ?? 0), "stat-active-subs"],
                ].map(([label, val, tid]) => (
                  <div key={tid} data-testid={tid} className="bg-black p-6">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-muted-eff mb-2">{label}</div>
                    <div className="font-display text-2xl">{val}</div>
                  </div>
                ))}
              </div>
              <div className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="eff-label">Revenus · {chartMode === "day" ? "30 derniers jours" : "par semaine"}</div>
                  <div className="flex gap-4">
                    {[["day", "Jour"], ["week", "Semaine"]].map(([k, l]) => (
                      <button key={k} data-testid={`chart-mode-${k}`} onClick={() => setChartMode(k)}
                        className={`text-[10px] tracking-[0.2em] uppercase ${chartMode === k ? "text-white" : "text-muted-eff hover:text-white"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div data-testid="revenue-chart" className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                      <XAxis dataKey="label" tick={{ fill: "#5a5a5a", fontSize: 10 }} axisLine={{ stroke: "#292929" }} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
                      <YAxis tick={{ fill: "#5a5a5a", fontSize: 10 }} axisLine={false} tickLine={false} width={44} />
                      <Tooltip cursor={{ fill: "#151515" }}
                        contentStyle={{ background: "#080808", border: "1px solid #292929", borderRadius: 0, fontSize: 12 }}
                        labelStyle={{ color: "#f7f7f5" }} itemStyle={{ color: "#f7f7f5" }}
                        formatter={(v) => [money(v, stats.totals?.currency), "Revenus"]} />
                      <Bar dataKey="total" fill="#f7f7f5" radius={[2, 2, 0, 0]} maxBarSize={chartMode === "day" ? 16 : 48} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="eff-label mb-4">Ventes par événement</div>
              <div className="border-t border-[#292929]">
                {(stats.events || []).length === 0 && <div className="py-6 text-muted-eff text-sm">Aucune vente de billet pour l'instant.</div>}
                {(stats.events || []).map((e) => (
                  <div key={e.event_id} data-testid={`stats-event-${e.event_id}`} className="border-b border-[#292929] py-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{e.title}</div>
                      <div className="text-[11px] text-muted-eff">
                        {e.tickets_sold}{e.quota ? ` / ${e.quota}` : ""} billet(s) · {e.orders} commande(s)
                      </div>
                    </div>
                    <div className="font-display text-lg shrink-0">{money(e.revenue, stats.totals.currency)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "orders" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] tracking-[0.2em] uppercase text-muted-eff">{orders.length} commande(s)</span>
            <Btn data-testid="export-csv" variant="outline" onClick={exportCsv}>Exporter CSV</Btn>
          </div>
          <div className="border-t border-[#292929]">
          {orders.length === 0 && <div className="py-6 text-muted-eff text-sm">Aucune commande.</div>}
          {orders.map((o) => (
            <div key={o.session_id} data-testid={`order-row-${o.session_id}`} className="border-b border-[#292929] py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm truncate">{o.kind === "ticket" ? `Billet · ${o.event_title || ""}` : `Abonnement · ${o.plan_name || ""}`}</div>
                <div className="text-[11px] text-muted-eff truncate">{o.user_email || o.user_id} · {new Date(o.created_at).toLocaleString("fr-CA")} · {money(o.amount, o.currency)}</div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[10px] tracking-widest uppercase text-muted-eff">{o.payment_status}</span>
                {o.payment_status === "paid" && o.email_status === "failed" && (
                  <span data-testid={`order-email-failed-${o.session_id}`} className="text-[10px] tracking-widest uppercase text-red-400">email échoué</span>
                )}
                {o.payment_status === "paid" && (
                  <Btn data-testid={`order-resend-${o.session_id}`} variant="ghost" onClick={() => resendEmail(o.session_id)}>Renvoyer l'email</Btn>
                )}
                {o.payment_status === "paid" && <Btn variant="ghost" onClick={() => refund(o.session_id)}>Rembourser</Btn>}
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      {tab === "tickets" && (
        <div className="border-t border-[#292929]">
          {tickets.length === 0 && <div className="py-6 text-muted-eff text-sm">Aucun billet.</div>}
          {tickets.map((t) => (
            <div key={t.id} className="border-b border-[#292929] py-4 flex items-center justify-between gap-4">
              <div><div className="text-sm">{t.event_title}</div><div className="text-[11px] text-muted-eff break-all">{t.qr_token}</div></div>
              <span className="text-[10px] tracking-widest uppercase text-muted-eff">{t.checked_in ? "Utilisé" : t.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "subs" && (
        <div className="border-t border-[#292929]">
          {subs.length === 0 && <div className="py-6 text-muted-eff text-sm">Aucun abonnement.</div>}
          {subs.map((s) => (
            <div key={s.id || s.stripe_subscription_id} data-testid={`commerce-sub-row-${s.id || s.stripe_subscription_id}`} className="border-b border-[#292929] py-4 flex items-center justify-between gap-4">
              <div><div className="text-sm">{s.plan_name}</div><div className="text-[11px] text-muted-eff">{s.user_email || s.user_id}</div></div>
              <div className="text-right">
                <span className="text-[10px] tracking-widest uppercase text-muted-eff">{s.status}</span>
                {s.current_period_end && (
                  <div data-testid="commerce-sub-renewal" className="text-[11px] text-muted-eff mt-1">
                    Renouvellement · {new Date(s.current_period_end).toLocaleDateString("fr-CA")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "checkin" && (
        <div className="max-w-md">
          <p className="text-muted-eff text-sm mb-4">Validez un billet par son token QR ou par la caméra. Le scan caméra reste actif en continu (validation serveur, usage unique).</p>
          <Btn data-testid="checkin-scan" variant="outline" className="inline-flex items-center gap-2 mb-4" onClick={() => { setScanResult(null); setScanning(true); }}>
            <ScanLine className="w-4 h-4" /> Scanner avec la caméra
          </Btn>
          <input data-testid="checkin-token" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Token du billet"
            className="w-full bg-[#050505] border border-[#292929] px-3 py-2.5 text-sm focus:outline-none focus:border-white mb-4" />
          <Btn data-testid="checkin-submit" variant="solid" onClick={submitToken}>Valider le billet</Btn>
          {scanning && <QrScanner onScan={handleScan} result={scanResult} onClose={() => { setScanning(false); setScanResult(null); }} />}
        </div>
      )}

      {tab === "history" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] tracking-[0.2em] uppercase text-muted-eff">{checkins.length} entrée(s) validée(s)</span>
            <Btn data-testid="checkins-refresh" variant="ghost" onClick={loadCheckins}>Rafraîchir</Btn>
          </div>
          <div className="border-t border-[#292929]">
            {checkins.length === 0 && <div className="py-6 text-muted-eff text-sm">Aucune entrée validée pour l'instant.</div>}
            {checkins.map((c) => (
              <div key={c.id} data-testid={`checkin-row-${c.id}`} className="border-b border-[#292929] py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm truncate">{c.event_title}</div>
                  <div className="text-[11px] text-muted-eff truncate">{c.user_email || c.user_id} · {c.checked_in_at ? new Date(c.checked_in_at).toLocaleString("fr-CA") : ""}</div>
                </div>
                <Btn data-testid={`undo-checkin-${c.id}`} variant="ghost" onClick={() => undoCheckin(c.qr_token)}>Annuler</Btn>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
